import { PropsWithChildren } from 'react';
import {
  GrcTextArea,
  GrcTextAreaLabelMode,
  GrcTextAreaStylingMode,
  GrcTextBox
} from '@grc/ui-package';
import { TenantCommonDetails } from './tenant-form';

interface TenantDetailsFieldsProps {
  values: TenantCommonDetails;
  setField: (field: keyof TenantCommonDetails) => (e: { value?: string | null }) => void;
  markTouched: (field: keyof TenantCommonDetails) => () => void;
  nameInvalid: boolean;
  carrierPortalUrlInvalid: boolean;
  customerPortalUrlInvalid: boolean;
}

/**
 * The address block, identical on both flows. Children land between Phone and
 * Notes — edit puts its Email editor there, create has none.
 */
export default function TenantDetailsFields({
  values,
  setField,
  markTouched,
  nameInvalid,
  carrierPortalUrlInvalid,
  customerPortalUrlInvalid,
  children
}: PropsWithChildren<TenantDetailsFieldsProps>) {
  return (
    <>
      <GrcTextBox
        id="tenant-name"
        label="Name"
        labelMode="static"
        stylingMode="outlined"
        className="full-width"
        value={values.name}
        onValueChanged={setField('name')}
        onFocusOut={markTouched('name')}
        isValid={!nameInvalid}
        validationError={{ message: 'Name is required.' }}
        validationMessageMode="auto"
      />

      <GrcTextBox
        id="tenant-street"
        label="Street Address"
        labelMode="static"
        stylingMode="outlined"
        className="full-width"
        value={values.streetAddress}
        onValueChanged={setField('streetAddress')}
      />

      <GrcTextBox
        id="tenant-city"
        label="City"
        labelMode="static"
        stylingMode="outlined"
        value={values.city}
        onValueChanged={setField('city')}
      />

      <GrcTextBox
        id="tenant-state"
        label="State"
        labelMode="static"
        stylingMode="outlined"
        value={values.state}
        onValueChanged={setField('state')}
      />

      <GrcTextBox
        id="tenant-postal"
        label="Postal Code"
        labelMode="static"
        stylingMode="outlined"
        value={values.postalCode}
        onValueChanged={setField('postalCode')}
      />

      <GrcTextBox
        id="tenant-country"
        label="Country"
        labelMode="static"
        stylingMode="outlined"
        value={values.country}
        onValueChanged={setField('country')}
      />

      <GrcTextBox
        id="tenant-phone"
        label="Phone"
        labelMode="static"
        stylingMode="outlined"
        mode="tel"
        value={values.phoneNumber}
        onValueChanged={setField('phoneNumber')}
      />

      {children}

      <GrcTextBox
        id="tenant-carrier-portal-url"
        label="Carrier Portal URL"
        labelMode="static"
        stylingMode="outlined"
        className="full-width"
        mode="url"
        placeholder="Leave blank to use the default for this environment"
        value={values.carrierPortalUrl}
        onValueChanged={setField('carrierPortalUrl')}
        onFocusOut={markTouched('carrierPortalUrl')}
        isValid={!carrierPortalUrlInvalid}
        validationError={{ message: 'Enter a full URL starting with https://' }}
        validationMessageMode="auto"
      />

      <GrcTextBox
        id="tenant-customer-portal-url"
        label="Customer Portal URL"
        labelMode="static"
        stylingMode="outlined"
        className="full-width"
        mode="url"
        placeholder="Leave blank to hide the Customer Portal link"
        value={values.customerPortalUrl}
        onValueChanged={setField('customerPortalUrl')}
        onFocusOut={markTouched('customerPortalUrl')}
        isValid={!customerPortalUrlInvalid}
        validationError={{ message: 'Enter a full URL starting with https://' }}
        validationMessageMode="auto"
      />

      <GrcTextArea
        id="tenant-notes"
        label="Notes"
        labelMode={GrcTextAreaLabelMode.Static}
        stylingMode={GrcTextAreaStylingMode.Outlined}
        className="full-width"
        autoResizeEnabled
        value={values.notes}
        onValueChanged={setField('notes')}
      />
    </>
  );
}
